// Browser-native thumbnail generator (spec #48, #51).
//
// Phase 1 deliberately avoids pulling in any image-processing
// dependency. The Web client uses the browser's `createImageBitmap`
// + an offscreen `<canvas>` to produce a 256×256 JPEG thumbnail.
//
// Behaviour:
//   - Target dimension: 256 on the longer edge, preserve aspect
//     ratio. The Backend constraint is 256 KiB and the spec target
//     is "256px JPEG quality 0.80" — both are met for typical phone
//     photos (~8–25 KiB).
//   - Quality: 0.80 (matches Mobile's ThumbnailGenerator).
//   - EXIF orientation: passed through `createImageBitmap`'s
//     `imageOrientation: 'from-image'` option where the browser
//     supports it. Unsupported browsers fall back silently — a
//     small subset of portrait photos may render sideways in the
//     gallery strip; acceptable Phase 1 trade-off.
//   - HEIC decode failure: returns `null`. The caller falls back to
//     no thumbnail; the Backend's `thumbnail_upload_url` will be
//     `null` and the original photo uploads normally.
//   - Any other failure (Canvas blocked, toBlob unsupported): also
//     returns `null`. Non-fatal by contract — see
//     `photoUploadService.ts`.

import type { GeneratedThumbnail } from './photoUploadTypes';

/** Max dimension for the longer edge of the thumbnail. */
export const THUMBNAIL_MAX_DIM = 256;
/** JPEG quality passed to `canvas.toBlob`. Matches Mobile. */
export const THUMBNAIL_JPEG_QUALITY = 0.8;

/**
 * Generate a thumbnail for a single File. Returns `null` on any
 * decode / render failure — the caller MUST treat `null` as "no
 * thumbnail" and continue the upload without `thumbnail_bytes`.
 */
export async function generateThumbnail(file: File): Promise<GeneratedThumbnail | null> {
  if (typeof createImageBitmap !== 'function') {
    return null;
  }
  if (typeof document === 'undefined') {
    // No DOM — should never happen in browser context, but a guard
    // lets this module be safely imported in tests that don't
    // exercise the actual decode path.
    return null;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, {
      // `from-image` is the only widely-supported value; browsers
      // that don't implement the option silently ignore it.
      imageOrientation: 'from-image',
    });
  } catch {
    // Decode failure (HEIC on Chromium / Firefox, corrupt JPEG,
    // unsupported format the browser can't handle). Caller skips
    // thumbnail_bytes; original upload still proceeds.
    return null;
  }

  try {
    const { width: srcW, height: srcH } = bitmap;
    if (srcW <= 0 || srcH <= 0) return null;

    const scale = Math.min(1, THUMBNAIL_MAX_DIM / Math.max(srcW, srcH));
    const dstW = Math.max(1, Math.round(srcW * scale));
    const dstH = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, dstW, dstH);

    const blob = await canvasToBlob(canvas, THUMBNAIL_JPEG_QUALITY);
    if (!blob) return null;

    return {
      blob,
      width: dstW,
      height: dstH,
      bytes: blob.size,
    };
  } finally {
    // Release the decoded bitmap immediately; we only need the
    // resulting Blob. Without this, an 8-photo multi-pick would
    // retain 8 large bitmaps in memory until the GC reclaims them.
    bitmap.close();
  }
}

/**
 * Promisified `canvas.toBlob`. jsdom does not implement toBlob, so
 * the wrapping lets tests stub it cleanly via dependency injection
 * (see `photoUploadService.ts`).
 */
function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });
}
